import { useNavigate, useLocation } from "react-router-dom";
import { authenticateClientPortal } from "@/actions/client-portal";
import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  createContext,
} from "react";

interface ClientAccessState {
  isAuthenticated: boolean;
  projectId: string | null;
  clientId: string | null;
  lastActivity: number;
  isLoading: boolean;
}

interface ClientAccessContextType {
  isAuthenticated: boolean;
  projectId: string | null;
  clientId: string | null;
  isLoading: boolean;
  authenticate: (
    projectId: string,
    clientId: string,
    password: string
  ) => Promise<boolean>;
  logout: () => void;
  refreshSession: () => void;
}

const ClientAccessContext = createContext<ClientAccessContextType | undefined>(
  undefined
);

// Session timeout: 30 minutes of inactivity
const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes in milliseconds
const STORAGE_KEY = "client_access_session";

interface ClientAccessProviderProps {
  children: React.ReactNode;
}

export const ClientAccessProvider: React.FC<ClientAccessProviderProps> = ({
  children,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [state, setState] = useState<ClientAccessState>(() => {
    // Try to restore session from localStorage
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        const now = Date.now();

        // Check if session is still valid
        if (
          parsed.lastActivity &&
          now - parsed.lastActivity < SESSION_TIMEOUT
        ) {
          return {
            isAuthenticated: true,
            projectId: parsed.projectId,
            clientId: parsed.clientId,
            lastActivity: now,
            isLoading: false,
          };
        }
      } catch (error) {
        console.error("Error parsing stored session:", error);
      }
    }

    return {
      isAuthenticated: false,
      projectId: null,
      clientId: null,
      lastActivity: Date.now(),
      isLoading: false,
    };
  });

  // Save session to localStorage
  const saveSession = useCallback(
    (sessionData: Partial<ClientAccessState>) => {
      const newState = { ...state, ...sessionData, lastActivity: Date.now() };
      setState(newState);

      if (newState.isAuthenticated && newState.projectId && newState.clientId) {
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({
            projectId: newState.projectId,
            clientId: newState.clientId,
            lastActivity: newState.lastActivity,
          })
        );
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    },
    [state]
  );

  // Refresh session activity
  const refreshSession = useCallback(() => {
    if (state.isAuthenticated) {
      saveSession({ lastActivity: Date.now() });
    }
  }, [state.isAuthenticated, saveSession]);

  // Logout function
  const logout = useCallback(() => {
    setState({
      isAuthenticated: false,
      projectId: null,
      clientId: null,
      lastActivity: Date.now(),
      isLoading: false,
    });
    localStorage.removeItem(STORAGE_KEY);

    // Redirect to client access gate with current project info if available
    const currentPath = location.pathname;
    const clientMatch = currentPath.match(
      /\/client\/([^/]+)\/project\/([^/]+)/
    );

    if (clientMatch) {
      const [, clientId, projectId] = clientMatch;
      navigate(`/client-access?projectId=${projectId}&clientId=${clientId}`);
    } else {
      navigate("/client-access");
    }
  }, [navigate, location.pathname]);

  // Authenticate function
  const authenticate = useCallback(
    async (
      projectId: string,
      clientId: string,
      password: string
    ): Promise<boolean> => {
      setState((prev) => ({ ...prev, isLoading: true }));

      try {
        const result = await authenticateClientPortal({ projectId, password });

        if (result.authenticated) {
          saveSession({
            isAuthenticated: true,
            projectId,
            clientId,
            isLoading: false,
          });
          return true;
        } else {
          setState((prev) => ({ ...prev, isLoading: false }));
          return false;
        }
      } catch (error) {
        console.error("Authentication error:", error);
        setState((prev) => ({ ...prev, isLoading: false }));
        return false;
      }
    },
    [saveSession]
  );

  // Check for session timeout
  useEffect(() => {
    if (!state.isAuthenticated) return;

    const checkTimeout = () => {
      const now = Date.now();
      const timeSinceLastActivity = now - state.lastActivity;

      if (timeSinceLastActivity >= SESSION_TIMEOUT) {
        logout();
      }
    };

    // Check every minute
    const interval = setInterval(checkTimeout, 60 * 1000);

    return () => clearInterval(interval);
  }, [state.isAuthenticated, state.lastActivity, logout]);

  // Track user activity to refresh session
  useEffect(() => {
    if (!state.isAuthenticated) return;

    const events = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
      "click",
    ];

    const handleActivity = () => {
      refreshSession();
    };

    // Throttle activity tracking to avoid excessive updates
    let throttleTimeout: NodeJS.Timeout;
    const throttledHandleActivity = () => {
      if (throttleTimeout) return;

      throttleTimeout = setTimeout(() => {
        handleActivity();
        throttleTimeout = null as any;
      }, 30000); // Update every 30 seconds max
    };

    events.forEach((event) => {
      document.addEventListener(event, throttledHandleActivity, true);
    });

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, throttledHandleActivity, true);
      });
      if (throttleTimeout) {
        clearTimeout(throttleTimeout);
      }
    };
  }, [state.isAuthenticated, refreshSession]);

  const value: ClientAccessContextType = {
    isAuthenticated: state.isAuthenticated,
    projectId: state.projectId,
    clientId: state.clientId,
    isLoading: state.isLoading,
    authenticate,
    logout,
    refreshSession,
  };

  return (
    <ClientAccessContext.Provider value={value}>
      {children}
    </ClientAccessContext.Provider>
  );
};

export const useClientAccess = (): ClientAccessContextType => {
  const context = useContext(ClientAccessContext);
  if (context === undefined) {
    throw new Error(
      "useClientAccess must be used within a ClientAccessProvider"
    );
  }
  return context;
};
